import React, { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { RoleBadge } from "@/components/RoleBadge";
import { Ribbon } from "lucide-react";
import { toast } from "sonner";

function RequestRow({ r, onDecided }) {
  const [note, setNote] = useState("");
  const act = async (fn, msg) => {
    try { await fn(r.id, note); toast.success(msg); onDecided(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };
  return (
    <div data-testid={`role-badge-req-${r.id}`} className="bg-white border border-neutral-200 rounded-xl p-4">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium text-forest text-sm">{r.user_name}</span>
        <Badge className="rounded-full text-[10px] bg-indigo-50 text-indigo-700 border border-indigo-200">{r.role_label}</Badge>
        {r.status !== "pending" && <Badge className="rounded-full text-[10px] bg-neutral-200 text-neutral-600 border-none">{r.status}</Badge>}
        <span className="text-xs text-neutral-400 ml-auto">{new Date(r.created_at).toLocaleDateString()}</span>
      </div>
      <div className="text-xs text-neutral-600 mt-2 space-y-0.5">
        <div>Work email: <span className="font-mono">{r.work_email}</span></div>
        <div>Evidence: {r.evidence_link
          ? <a href={r.evidence_link} target="_blank" rel="noreferrer" className="text-forest underline break-all">{r.evidence_link}</a>
          : <span className="text-neutral-400">no link provided</span>}</div>
      </div>
      {r.status === "pending" && (
        <div className="flex gap-2 mt-3 flex-wrap items-center">
          <Input data-testid={`role-badge-note-${r.id}`} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Note (optional, shared on decline)" className="h-8 text-xs max-w-xs" />
          <Button data-testid={`role-badge-approve-${r.id}`} onClick={() => act(api.approveRoleBadge, "Badge approved")}
            size="sm" className="rounded-full h-8 text-xs bg-forest hover:bg-forest-hover">Approve</Button>
          <Button data-testid={`role-badge-decline-${r.id}`} onClick={() => act(api.declineRoleBadge, "Request declined")}
            variant="outline" size="sm" className="rounded-full h-8 text-xs border-red-200 text-red-600 hover:bg-red-50">Decline</Button>
        </div>
      )}
    </div>
  );
}

export function RoleBadgesPanel() {
  const [reqs, setReqs] = useState([]);
  const [holders, setHolders] = useState([]);
  const [showAll, setShowAll] = useState(false);

  const load = () => {
    api.adminRoleBadges(showAll ? "all" : "pending").then(setReqs).catch(() => {});
    api.adminRoleBadgeHolders().then(setHolders).catch(() => {});
  };
  useEffect(() => { load(); }, [showAll]); // eslint-disable-line react-hooks/exhaustive-deps -- render-scoped fetch helper

  const revoke = async (u) => {
    try {
      await api.revokeRoleBadge(u.user_id);
      toast(`Badge removed from ${u.name}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not revoke"); }
  };

  const pendingCount = reqs.filter(r => r.status === "pending").length;
  return (
    <div className="mt-14" data-testid="role-badges-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display text-2xl text-forest tracking-tight flex items-center gap-2">
          <Ribbon className="w-5 h-5" /> Role badges {pendingCount > 0 && (
            <Badge className="rounded-full bg-indigo-100 text-indigo-700 border-none">{pendingCount} pending</Badge>
          )}
        </h2>
        <button data-testid="role-badges-toggle-all" onClick={() => setShowAll(s => !s)} className="text-xs text-neutral-500 hover:text-forest underline">
          {showAll ? "Show pending only" : "Show all requests"}
        </button>
      </div>
      <p className="text-neutral-600 text-sm mt-1">
        Civic role badge requests — check the work email domain and the linked public page. Display-only, no powers.
      </p>
      <div className="mt-5 space-y-3">
        {reqs.length === 0 && <div className="text-sm text-neutral-500">No {showAll ? "" : "pending "}requests.</div>}
        {reqs.map(r => <RequestRow key={r.id} r={r} onDecided={load} />)}
      </div>
      {holders.length > 0 && (
        <div className="mt-6" data-testid="role-badge-holders">
          <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Current badge holders</div>
          <div className="mt-2 space-y-2">
            {holders.map(u => (
              <div key={u.user_id} data-testid={`role-badge-holder-${u.user_id}`} className="bg-white border border-neutral-200 rounded-xl px-4 py-2.5 flex items-center gap-3">
                <span className="text-sm font-medium text-forest">{u.name}</span>
                <RoleBadge badge={u.role_badge} />
                <button data-testid={`role-badge-revoke-${u.user_id}`} onClick={() => revoke(u)}
                  className="ml-auto text-xs text-red-600 hover:underline">Revoke</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
